import { useCallback, useState } from 'react';

import type { IPredicate } from '@feathr/blackbox';

import type { IDonationCreatedEvent } from '../../hooks';
import type { ITransactionFilters } from './TransactionFilters.types';
import { DEFAULT_FILTERS } from './TransactionFilters.types';
import {
  buildPredicates as buildPredicatesUtil,
  getActiveFilterCount,
  matchesFilters as matchesFiltersUtil,
} from './TransactionFilters.utils';

interface IUseTransactionFiltersReturn {
  /** Current filter values */
  filters: ITransactionFilters;
  /** Update a single filter field */
  setFilter: <K extends keyof ITransactionFilters>(key: K, value: ITransactionFilters[K]) => void;
  /** Replace all filters at once */
  setFilters: (newFilters: ITransactionFilters) => void;
  /** Reset all filters to defaults */
  clearFilters: () => void;
  /** Count of active filters (for "Clear (N)" button) */
  activeFilterCount: number;
  /** Build predicates for Breadcrumbs API */
  buildPredicates: () => IPredicate[];
  /**
   * Build predicates ignoring the donor filter — used to source the donor
   * dropdown's default options so it isn't constrained by the very filter
   * the user is editing.
   */
  buildPredicatesWithoutDonors: () => IPredicate[];
  /** Check if a real-time event matches current filters */
  matchesFilters: (event: IDonationCreatedEvent) => boolean;
}

/**
 * Hook for managing transaction table filter state.
 *
 * @returns Filter state and utilities
 *
 * @example
 * ```tsx
 * const { filters, setFilter, clearFilters, buildPredicates, matchesFilters } = useTransactionFilters();
 *
 * // Update a filter
 * setFilter('formIds', ['form-1', 'form-2']);
 *
 * // Get API predicates
 * const predicates = buildPredicates();
 *
 * // Filter real-time events
 * if (matchesFilters(event)) {
 *   addItem(event);
 * }
 * ```
 */
function useTransactionFilters(): IUseTransactionFiltersReturn {
  const [filters, setFilters] = useState<ITransactionFilters>(DEFAULT_FILTERS);

  const setFilter = useCallback(
    <K extends keyof ITransactionFilters>(key: K, value: ITransactionFilters[K]): void => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const clearFilters = useCallback((): void => {
    setFilters(DEFAULT_FILTERS);
  }, []);

  const activeFilterCount = getActiveFilterCount(filters);

  const buildPredicates = useCallback((): IPredicate[] => {
    return buildPredicatesUtil(filters);
  }, [filters]);

  const buildPredicatesWithoutDonors = useCallback((): IPredicate[] => {
    return buildPredicatesUtil({ ...filters, donors: [] });
  }, [filters]);

  const matchesFilters = useCallback(
    (event: IDonationCreatedEvent): boolean => {
      return matchesFiltersUtil(event, filters);
    },
    [filters],
  );

  return {
    filters,
    setFilter,
    setFilters,
    clearFilters,
    activeFilterCount,
    buildPredicates,
    buildPredicatesWithoutDonors,
    matchesFilters,
  };
}

export default useTransactionFilters;
export type { IUseTransactionFiltersReturn };
